import { useLocation, useNavigate } from "@tanstack/react-router";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import { useOrganization } from "@/contexts/organization-context";
import { loadStoredTabs, saveTabsToStorage } from "@/routing/core/persistence";
import { createTabsStore, type TabsSnapshot } from "@/routing/core/tabs-store";

export type {
  CloseTabResult,
  GuardedTabNavigationOptions,
  NavigationGuardCallback,
  NavigationGuardRequest,
  TabNavigationOptions,
} from "@/routing/core/tabs-store";

type TabsStateContextValue = TabsSnapshot;
type TabsStore = ReturnType<typeof createTabsStore>;
type TabsActionsContextValue = Pick<
  TabsStore,
  | "openProjectTab"
  | "openAdminTab"
  | "closeTab"
  | "forceCloseTab"
  | "setActiveTab"
  | "goHome"
  | "isTabOpen"
  | "navigateTab"
  | "updateTabSearchParams"
  | "getTab"
  | "registerNavigationGuard"
  | "unregisterNavigationGuard"
>;
type TabsInternalActionsContextValue = Pick<
  TabsStore,
  "renameTab" | "updateTabMemory" | "registerNavigationPolicy" | "unregisterNavigationPolicy"
>;

const TabsStateContext = createContext<TabsStateContextValue | null>(null);
const TabsActionsContext = createContext<TabsActionsContextValue | null>(null);
const TabsStoreContext = createContext<TabsStore | null>(null);

export function TabsProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    organizationId,
    isLoading: isOrganizationLoading,
    organizationSwitchRequest,
  } = useOrganization();
  const navigateRef = useRef(navigate);
  const storeRef = useRef<TabsStore | null>(null);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  if (!storeRef.current) {
    storeRef.current = createTabsStore({
      commitUrl: (args) =>
        navigateRef.current({
          to: args.to,
          search: args.search,
          replace: args.replace,
        }),
      initialLocation: {
        pathname: location.pathname,
        search: location.search as Record<string, unknown>,
      },
      loadTabs: loadStoredTabs,
      saveTabs: saveTabsToStorage,
    });
  }

  const store = storeRef.current;

  // The store owns the org-change behavior; this only forwards it. `keepCurrentLocation`
  // is honored solely for the switch that actually asked for it: the request records one
  // specific `orgId`, so a later dropdown switch (which records `keepCurrentLocation: false`)
  // cannot inherit an earlier deep-link switch's flag.
  useEffect(() => {
    if (isOrganizationLoading) {
      return;
    }
    store.handleOrganizationChange(organizationId, {
      keepCurrentLocation:
        organizationSwitchRequest?.orgId === organizationId &&
        organizationSwitchRequest.keepCurrentLocation,
    });
  }, [isOrganizationLoading, organizationId, organizationSwitchRequest, store]);

  const stateValue = useSyncExternalStore(store.subscribe, store.getSnapshot);

  const actionsValue = useMemo<TabsActionsContextValue>(
    () => ({
      openProjectTab: store.openProjectTab,
      openAdminTab: store.openAdminTab,
      closeTab: store.closeTab,
      forceCloseTab: store.forceCloseTab,
      setActiveTab: store.setActiveTab,
      goHome: store.goHome,
      isTabOpen: store.isTabOpen,
      navigateTab: store.navigateTab,
      updateTabSearchParams: store.updateTabSearchParams,
      getTab: store.getTab,
      registerNavigationGuard: store.registerNavigationGuard,
      unregisterNavigationGuard: store.unregisterNavigationGuard,
    }),
    [store]
  );

  return (
    <TabsStoreContext.Provider value={store}>
      <TabsStateContext.Provider value={stateValue}>
        <TabsActionsContext.Provider value={actionsValue}>{children}</TabsActionsContext.Provider>
      </TabsStateContext.Provider>
    </TabsStoreContext.Provider>
  );
}

export function useTabsState() {
  const context = useContext(TabsStateContext);
  if (!context) {
    throw new Error("useTabs must be used within a TabsProvider");
  }
  return context;
}

export function useTabsActions() {
  const context = useContext(TabsActionsContext);
  if (!context) {
    throw new Error("useTabs must be used within a TabsProvider");
  }
  return context;
}

export function useTabsStore() {
  const context = useContext(TabsStoreContext);
  if (!context) {
    throw new Error("useTabsStore must be used within a TabsProvider");
  }
  return context;
}

export function useTabsInternalActions(): TabsInternalActionsContextValue {
  const store = useTabsStore();
  return useMemo(
    () => ({
      renameTab: store.renameTab,
      updateTabMemory: store.updateTabMemory,
      registerNavigationPolicy: store.registerNavigationPolicy,
      unregisterNavigationPolicy: store.unregisterNavigationPolicy,
    }),
    [store]
  );
}

export function useTabs() {
  const state = useTabsState();
  const actions = useTabsActions();

  return useMemo(
    () => ({
      ...state,
      ...actions,
    }),
    [state, actions]
  );
}
